import React, { useState, useEffect } from "react";
import { MessageSquare, Phone, ArrowRight, RefreshCcw, AlertCircle, CheckCircle2, Shield } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

interface WhatsAppLoginFormProps {
  mobileNumber: string;
  otp: string;
  loading: boolean;
  helperText?: string;
  onMobileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onOTPChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSendOTP: () => Promise<boolean | void>;
  onVerifyOTP: (e: React.FormEvent) => void;
}

const WhatsAppLoginForm: React.FC<WhatsAppLoginFormProps> = ({
  mobileNumber,
  otp,
  loading,
  helperText,
  onMobileChange,
  onOTPChange,
  onSendOTP,
  onVerifyOTP,
}) => {
  const [otpSent, setOtpSent] = useState(false);
  const [timer, setTimer] = useState(30);
  const [timerActive, setTimerActive] = useState(false);
  const [localError, setLocalError] = useState("");

  useEffect(() => {
    let interval: any = null;
    if (timerActive && timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else if (timer === 0) {
      setTimerActive(false);
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [timerActive, timer]);

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mobileNumber.trim()) {
      setLocalError("Please enter your registered mobile number or username.");
      return;
    }
    setLocalError("");
    const success = await onSendOTP();
    if (success !== false) {
      setOtpSent(true);
      setTimer(30);
      setTimerActive(true);
    }
  };

  const handleResendOTP = async () => {
    if (timerActive) return;
    setLocalError("");
    const success = await onSendOTP();
    if (success !== false) {
      setTimer(30);
      setTimerActive(true);
    }
  };

  const handleChangeNumber = () => {
    setOtpSent(false);
    setTimerActive(false);
    setLocalError("");
  };

  const SafeAnimatePresence = AnimatePresence as any;

  return (
    <div className="w-full">
      <SafeAnimatePresence mode="wait">
        {!otpSent ? (
          <motion.form
            key="send-whatsapp-otp"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-5"
            onSubmit={handleSendCode}
          >
            <div className="space-y-2">
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider ml-1">
                Mobile Number / Username
              </label>
              <div className="relative group">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-emerald-600 group-focus-within:text-emerald-500 transition-colors">
                  <Phone size={18} />
                </div>
                <input
                  type="text"
                  value={mobileNumber}
                  onChange={(e) => {
                    setLocalError("");
                    onMobileChange(e);
                  }}
                  placeholder="e.g. 9876543210 or username"
                  className={`w-full pl-12 pr-4 py-3.5 bg-emerald-50/40 border-2 rounded-2xl outline-none transition-all duration-200 ${
                    localError || helperText
                      ? "border-rose-200 bg-rose-50/30 focus:border-rose-400"
                      : "border-transparent focus:border-emerald-500 focus:bg-white focus:shadow-lg focus:shadow-emerald-500/10"
                  }`}
                />
              </div>
              {(localError || helperText) && (
                <motion.p
                  initial={{ opacity: 0, y: -5 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-xs text-rose-500 mt-2 flex items-center gap-1.5 font-medium ml-1"
                >
                  <AlertCircle size={14} /> {localError || helperText}
                </motion.p>
              )}
            </div>

            <div className="p-3.5 rounded-2xl bg-emerald-50/80 border border-emerald-100 flex items-center gap-3">
              <div className="p-2 rounded-xl bg-emerald-500 text-white shrink-0">
                <MessageSquare size={16} />
              </div>
              <p className="text-xs text-emerald-900 font-medium leading-relaxed">
                We will send a 6-digit one-time security code to your WhatsApp account.
              </p>
            </div>

            <div className="pt-2">
              <button
                type="submit"
                disabled={loading}
                className="w-full group relative py-4 px-6 bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white font-bold rounded-2xl shadow-xl shadow-emerald-600/20 hover:shadow-emerald-600/35 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 disabled:opacity-50 disabled:pointer-events-none flex items-center justify-center gap-2 overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/15 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />
                {loading ? (
                  <div className="w-5 h-5 border-3 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <MessageSquare size={18} className="text-white" />
                    <span>Send Code via WhatsApp</span>
                    <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </button>
            </div>
          </motion.form>
        ) : (
          <motion.form
            key="verify-whatsapp-otp"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-5"
            onSubmit={onVerifyOTP}
          >
            <div className="p-4 rounded-2xl bg-emerald-50 border border-emerald-200/70 flex items-start gap-3">
              <CheckCircle2 size={20} className="text-emerald-600 shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-xs font-bold text-emerald-900">OTP Sent Successfully</p>
                <p className="text-xs text-emerald-700 font-medium mt-0.5">
                  Verification code sent to WhatsApp: <span className="font-bold">{mobileNumber}</span>
                </p>
              </div>
              <button
                type="button"
                onClick={handleChangeNumber}
                className="text-xs font-bold text-emerald-700 hover:text-emerald-900 underline shrink-0"
              >
                Change
              </button>
            </div>

            <div className="space-y-2">
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider ml-1">
                Enter 6-Digit WhatsApp Code
              </label>
              <div className="relative group">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-emerald-600 group-focus-within:text-emerald-500 transition-colors">
                  <Shield size={18} />
                </div>
                <input
                  type="text"
                  maxLength={6}
                  value={otp}
                  onChange={onOTPChange}
                  placeholder="------"
                  className={`w-full pl-12 pr-4 py-3.5 bg-slate-50 border-2 rounded-2xl outline-none text-center tracking-[0.4em] font-mono text-lg font-bold transition-all duration-200 ${
                    helperText
                      ? "border-rose-200 bg-rose-50/30 focus:border-rose-400"
                      : "border-transparent focus:border-emerald-500 focus:bg-white focus:shadow-lg focus:shadow-emerald-500/10"
                  }`}
                />
              </div>
              {helperText && (
                <motion.p
                  initial={{ opacity: 0, y: -5 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-xs text-rose-500 mt-2 flex items-center gap-1.5 font-medium ml-1"
                >
                  <AlertCircle size={14} /> {helperText}
                </motion.p>
              )}
            </div>

            <div className="flex items-center justify-between px-1 text-xs">
              <span className="text-slate-500 font-medium">Didn't receive the code?</span>
              <button
                type="button"
                disabled={timerActive || loading}
                onClick={handleResendOTP}
                className={`font-bold flex items-center gap-1.5 transition-colors ${
                  timerActive
                    ? "text-slate-400 cursor-not-allowed"
                    : "text-emerald-600 hover:text-emerald-700"
                }`}
              >
                <RefreshCcw size={13} className={loading ? "animate-spin" : ""} />
                {timerActive ? `Resend in ${timer}s` : "Resend via WhatsApp"}
              </button>
            </div>

            <div className="pt-2 space-y-3">
              <button
                type="submit"
                disabled={loading || !otp.trim()}
                className="w-full group relative py-4 px-6 bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white font-bold rounded-2xl shadow-xl shadow-emerald-600/20 hover:shadow-emerald-600/35 transition-all duration-200 disabled:opacity-50 disabled:pointer-events-none flex items-center justify-center gap-2 overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/15 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />
                {loading ? (
                  <div className="w-5 h-5 border-3 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <span>Verify & Log In</span>
                    <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={handleChangeNumber}
                className="w-full py-2 text-xs font-bold text-slate-400 hover:text-slate-600 transition-colors text-center"
              >
                ← Back to Mobile Entry
              </button>
            </div>
          </motion.form>
        )}
      </SafeAnimatePresence>
    </div>
  );
};

export default WhatsAppLoginForm;
